import {
	CloseHandleV2,
	FocusLayerSizeV2,
	LayerManagerV2,
} from '@volkswagen-onehub/layer-manager';
import React, { useCallback } from 'react';
import { getEapiAvailability } from 'src/bff';
import { AvailabilityErrorLayer } from 'src/components';
import { LayerManagerWrapper } from 'src/feature-app';
import { createNoDmsSlotAndAssessor } from 'src/feature-app/NewMap';
import { LoadingLayer } from 'src/forms/CitaPosventa/NuevaCitaPosventa';
import { getFirstAndLastDayOfMonth } from 'src/helpers';
import {
	Assessors,
	Availability,
	CalendarSlot,
	DealersData,
	DmsInfo,
	Points,
} from 'src/types';

export interface AvailabilityHistory {
	dealerkvps: string;
	availabilityHistory: AvailabilityHistoryDealer[];
}

export interface AvailabilityHistoryDealer {
	dealer: DmsInfo;
	from: number;
	to: number;
	availability: Availability;
}

const checkIfAvailabilityIsInCache = (
	availabilityHistory: AvailabilityHistory[],
	dealer: DealersData,
	start: number,
): Availability | null => {
	const dealerHistory = availabilityHistory.find(
		a => a.dealerkvps === dealer.kvps,
	);

	if (dealerHistory) {
		const availabiliyDealer = dealerHistory.availabilityHistory.find(
			dealerAv => dealerAv.from === start || start < dealerAv.to,
		);
		return availabiliyDealer ? availabiliyDealer.availability : null;
	}
	return null;
};

const getAvailabilityFromDealer = (
	availabilityHistory: AvailabilityHistory[],
	kvps: string,
): AvailabilityHistory | null => {
	const dealerHistory = availabilityHistory.find(a => a.dealerkvps === kvps);
	return dealerHistory ? dealerHistory : null;
};

const saveAvailabilityInCache = (
	availabilityHistory: React.MutableRefObject<AvailabilityHistory[]>,
	dealer: DealersData,
	start: number,
	end: number,
	availability: Availability,
) => {
	const dealerInAvailability = availability.content.dealer;
	const availabilityHistoryDealer: AvailabilityHistoryDealer = {
		dealer: dealerInAvailability ? dealerInAvailability : dealer.dmsInfo,
		from: start,
		to: end,
		availability,
	};

	const dealerHistoryIndex = availabilityHistory.current.findIndex(
		a => a.dealerkvps === dealer.kvps,
	);
	if (dealerHistoryIndex !== -1) {
		availabilityHistory.current[
			dealerHistoryIndex
		].availabilityHistory.push(availabilityHistoryDealer);
	} else {
		availabilityHistory.current = [
			...availabilityHistory.current,
			{
				dealerkvps: dealer.kvps,
				availabilityHistory: [availabilityHistoryDealer],
			},
		];
	}
};

const getRandomInt = (max: number): number => {
	const min = 1;
	max = Math.floor(max);
	return Math.floor(Math.random() * (max - min + 1)) + min;
};

const findAssessor = (
	slot: CalendarSlot,
	assessors: Assessors[],
): Assessors => {
	return assessors.find(assessor => {
		return (
			assessor.name === slot.assessor ||
			assessor.code === slot.assessorCode
		);
	});
};

export const createAvailabilityManager = (
	layerManager: LayerManagerV2,
	store: any,
	env: any,
	setFirstAvailability: (availability: Availability) => void,
	setError: (error: boolean) => void,
	availabilityHistory: React.MutableRefObject<AvailabilityHistory[]>,
	loadingLayer: React.MutableRefObject<any>,
) => {
	const showLoadingLayer = () => {
		const renderLoadingLayer = () => (
			<LayerManagerWrapper store={store} env={env}>
				<LoadingLayer copy="Consultando con la agenda del taller" />
			</LayerManagerWrapper>
		);
		const layer = layerManager.openFocusLayer(
			renderLoadingLayer,
			{},
			{ size: FocusLayerSizeV2.A },
		);
		loadingLayer.current = layer;
	};

	const hideLoadingLayer = () => {
		if (loadingLayer.current) {
			loadingLayer.current.close();
			loadingLayer.current = null;
		}
	};

	const handleAvailabilityErrorLayer = () => {
		hideLoadingLayer();
		setError(true);
		const renderAvailabilityErrorLayer = (
			state: any,
			close: CloseHandleV2<any, any>,
		) => (
			<LayerManagerWrapper store={store} env={env}>
				<AvailabilityErrorLayer closeLayerCallback={close} />
			</LayerManagerWrapper>
		);
		layerManager.openFocusLayer(
			renderAvailabilityErrorLayer,
			{},
			{ size: FocusLayerSizeV2.A },
		);
	};

	const getAvailability = async (
		dealer: DealersData,
		start: number,
		end: number,
	): Promise<Availability> => {
		const cachedAvailability = checkIfAvailabilityIsInCache(
			availabilityHistory.current,
			dealer,
			start,
		);

		if (cachedAvailability) {
			setFirstAvailability(cachedAvailability);
			return cachedAvailability;
		} else {
			showLoadingLayer();
			if (availabilityHistory.current.length === 0) {
				const { firstDayOfMonth, lastDayOfMonth } =
					getFirstAndLastDayOfMonth();
				start = firstDayOfMonth;
				end = lastDayOfMonth;
			}

			const response = await getEapiAvailability(
				dealer.kvps,
				dealer.codInstalacion,
				start,
				end,
			);
			if (!response) {
				handleAvailabilityErrorLayer();
				return null;
			}

			const availability = response?.data;
			setError(false);
			saveAvailabilityInCache(
				availabilityHistory,
				dealer,
				start,
				end,
				availability,
			);
			hideLoadingLayer();

			if (availability.content?.calendar) {
				setFirstAvailability(availability);
				return availability;
			} else {
				return null;
			}
		}
	};

	return {
		getAvailability,
	};
};

export const handleDmsMapExit = (
	slot: CalendarSlot | CalendarSlot[],
	point: Points,
	availabilityHistory: AvailabilityHistory[],
	onComplete: (payload: any) => void,
) => {
	let { dealer } = point.properties;
	const dealerAvailability = getAvailabilityFromDealer(
		availabilityHistory,
		dealer.kvps,
	);

	if (
		dealerAvailability &&
		dealerAvailability.availabilityHistory[0].dealer
	) {
		const dealerFromDms = dealerAvailability.availabilityHistory[0].dealer;
		dealer = { ...dealer, dmsInfo: dealerFromDms };
	}

	const { assessors } = dealer.dmsInfo;
	let assessor: Assessors;

	if (Array.isArray(slot)) {
		const max = slot.length;
		const index = getRandomInt(max) - 1;
		assessor = findAssessor(slot[index], assessors);
	} else {
		assessor = findAssessor(slot, assessors);
	}

	if (!assessor) {
		assessor = {
			code: 'Undefined',
			name: 'Todos',
		};
	}

	onComplete({ assessor, slot, dealer });
};

export const handleNoDmsMapExit = (
	franjaHoraria: string,
	day: string,
	point: Points,
	onComplete: (payload: any) => void,
	availabilityHistory?: AvailabilityHistory[],
) => {
	let { dealer } = point.properties;

	if (availabilityHistory) {
		const dealerAvailability = getAvailabilityFromDealer(
			availabilityHistory,
			dealer.kvps,
		);
		if (
			dealerAvailability &&
			dealerAvailability.availabilityHistory[0].dealer
		) {
			const dealerFromDms =
				dealerAvailability.availabilityHistory[0].dealer;
			dealer = { ...dealer, dmsInfo: dealerFromDms };
		}
	}

	// Validamos que la cita a crear sea sin DMS mediante el flag dmsAvailable en false
	if (dealer && dealer.dmsInfo) {
		dealer = {
			...dealer,
			dmsInfo: { ...dealer.dmsInfo, dmsAvailable: false },
		};
	}

	const { slot, assessor } = createNoDmsSlotAndAssessor(franjaHoraria, day);

	onComplete({ slot, assessor, dealer });
};

export const createShowCalendarHandler = (
	getAvailability: any,
	handleSelectedPoint: any,
	setShowCalendar: any,
) => {
	return useCallback(
		async (point: Points) => {
			const { firstDayOfMonth, lastDayOfMonth } =
				getFirstAndLastDayOfMonth();
			await getAvailability(
				point.properties.dealer,
				firstDayOfMonth,
				lastDayOfMonth,
			);
			handleSelectedPoint(point);
			setShowCalendar(true);
		},
		[getAvailability, handleSelectedPoint],
	);
};

export const createHideCalendarHandler = (
	handleSelectedPoint: any,
	setShowCalendar: any,
) => {
	return () => {
		handleSelectedPoint(null);
		setShowCalendar(false);
	};
};
